import { getSessionStorageItem, setSessionStorageItem } from '@/lib/storage';

const RELOAD_AT_KEY = 'vite:stale-chunk-reload';
const RELOAD_COOLDOWN_MS = 10_000;

const DYNAMIC_IMPORT_FAILURE =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

type ReloadPage = () => void;

let listening = false;
let abort: AbortController | null = null;
let reloadPage: ReloadPage = () => {
    window.location.reload();
};

export function isDynamicImportFailure(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');

    return DYNAMIC_IMPORT_FAILURE.test(message);
}

export function recoverFromStaleChunk(error?: unknown): boolean {
    if (error !== undefined && !isDynamicImportFailure(error)) {
        return false;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    const lastReloadAt = Number(getSessionStorageItem(RELOAD_AT_KEY) ?? '0');
    const now = Date.now();

    if (now - lastReloadAt < RELOAD_COOLDOWN_MS) {
        return false;
    }

    setSessionStorageItem(RELOAD_AT_KEY, String(now));
    reloadPage();

    return true;
}

export async function withStaleChunkRecovery<T>(load: () => Promise<T>): Promise<T> {
    try {
        return await load();
    } catch (error) {
        recoverFromStaleChunk(error);
        throw error;
    }
}

export function listenForStaleChunkReload(reload: ReloadPage = () => window.location.reload()): void {
    if (listening || typeof window === 'undefined') {
        return;
    }

    listening = true;
    reloadPage = reload;
    abort = new AbortController();
    const { signal } = abort;

    window.addEventListener(
        'vite:preloadError',
        (event) => {
            event.preventDefault();
            recoverFromStaleChunk();
        },
        { signal },
    );

    window.addEventListener(
        'unhandledrejection',
        (event) => {
            if (!isDynamicImportFailure(event.reason)) {
                return;
            }

            event.preventDefault();
            recoverFromStaleChunk(event.reason);
        },
        { signal },
    );
}

export function resetStaleChunkReload(): void {
    abort?.abort();
    abort = null;
    listening = false;
    reloadPage = () => {
        window.location.reload();
    };
}
